'use client';

import { useMemo } from 'react';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import type { RegisterParticipantItem } from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { DataGrid, DataGridContainer } from '@/components/ui/data-grid';
import { DataGridTable } from '@/components/ui/data-grid-table';
import { LocaleLink } from '@/components/locale-link';
import { calculateAge, formatAge } from './companyDetailUtils';

/**
 * Table of Geschäftsführer / Vorstand / etc. (excluding owner roles — those go to Gesellschafter).
 * Organization managers (e.g. a Verwaltungs GmbH) that resolve to a known company are linked.
 * The age column is only shown to signed-in users.
 */
export function ManagersTabContent({
  managers,
  isAuthenticated,
}: {
  managers: RegisterParticipantItem[];
  isAuthenticated: boolean;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const columns = useMemo<ColumnDef<RegisterParticipantItem>[]>(
    () => [
      {
        id: 'name',
        header: t('common.labels.name'),
        cell: (info) => {
          const p = info.row.original;
          const name =
            [p.title, p.first_name, p.last_name].filter(Boolean).join(' ') ||
            p.organization;
          // When an organization manager (e.g. Verwaltungs GmbH on a GmbH &
          // Co. KG) was resolved to a known company, render it as a link to
          // /company/<slug> just like Subsidiaries / Shareholders do.
          const hasLink =
            !!p.organization &&
            !!p.eu_id &&
            !!p.register_court &&
            !!p.register_type &&
            !!p.register_id;
          if (hasLink && name) {
            return (
              <LocaleLink
                href={generateCompanySlug(
                  {
                    name,
                    sitz: p.city ?? null,
                    registerCourt: p.register_court ?? null,
                    registerType: p.register_type ?? null,
                    registerId: p.register_id ?? null,
                  },
                  locale,
                )}
                className="font-semibold text-xs break-words whitespace-normal text-primary hover:underline"
              >
                {name}
              </LocaleLink>
            );
          }
          return (
            <span className="font-semibold text-xs break-words whitespace-normal">
              {name}
            </span>
          );
        },
        size: 150,
      },
      {
        id: 'role',
        header: t('companyDetail.registerType'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.role || '—'}
          </span>
        ),
        size: 120,
      },
      ...(isAuthenticated
        ? [
            {
              id: 'age',
              header: t('companyDetail.age'),
              cell: (info: { row: { original: RegisterParticipantItem } }) => {
                const p = info.row.original;
                const age = calculateAge(p.date_of_birth) ?? p.age ?? null;
                return (
                  <span className="text-xs text-muted-foreground">
                    {formatAge(age, t('companyDetail.age'))}
                  </span>
                );
              },
              size: 100,
            },
          ]
        : []),
      {
        id: 'city',
        header: t('companyDetail.city'),
        cell: (info) => (
          <span className="text-xs break-words whitespace-normal">
            {info.row.original.city || '—'}
          </span>
        ),
        size: 100,
      },
    ],
    [t, isAuthenticated, locale],
  );

  const table = useReactTable({
    data: managers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataGridContainer
      border={false}
      className="max-h-[400px] overflow-hidden flex flex-col min-w-0 [&_th:first-child]:pl-5 [&_td:first-child]:pl-5 [&_th:last-child]:pr-5 [&_td:last-child]:pr-5"
    >
      <DataGrid<RegisterParticipantItem>
        table={table}
        recordCount={managers.length}
        tableLayout={{
          headerSticky: true,
          headerBorder: true,
          rowBorder: true,
          stripped: false,
          dense: true,
          width: 'auto',
          columnsResizable: false,
          cellBorder: false,
        }}
        tableClassNames={{
          base: 'w-full',
          header: 'text-xs',
          headerRow: 'h-8 !bg-muted',
          body: 'text-xs text-foreground font-normal',
          bodyRow: 'h-auto hover:bg-muted/30 transition-colors',
        }}
      >
        <div className="flex-1 min-h-0 overflow-auto">
          <DataGridTable />
        </div>
      </DataGrid>
    </DataGridContainer>
  );
}
